import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import type { TemplateSummary } from '../../models/template.models';
import { GlobalFeedbackService } from '../../services/global-feedback.service';
import { TemplateApiService } from '../../services/template-api.service';
import { UserDateTimeFormatService } from '../../services/user-date-time-format.service';
import { normalizeApiError } from '../../utils/error-normalizer';

@Component({
  selector: 'app-templates-list',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './templates-list.component.html',
  styleUrl: './templates-list.component.scss',
})
export class TemplatesListComponent implements OnInit {
  templates: TemplateSummary[] = [];

  searchQuery = '';
  appliedSearch = '';

  readonly availableLimits = [10, 20, 50, 100];
  page = 1;
  limit = 10;
  total = 0;
  totalPages = 1;
  goToPageInput = '1';

  loading = false;
  errorMessage = '';

  private readonly api = inject(TemplateApiService);
  private readonly feedback = inject(GlobalFeedbackService);
  private readonly dateTimeFormat = inject(UserDateTimeFormatService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router, { optional: true });

  ngOnInit(): void {
    this.loadTemplates();
  }

  applySearch(): void {
    this.page = 1;
    this.appliedSearch = this.searchQuery.trim();
    this.loadTemplates();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.appliedSearch = '';
    this.page = 1;
    this.loadTemplates();
  }

  totalLabel(): string {
    return this.total === 1 ? 'Template' : 'Templates';
  }

  onRowClick(templateId: string, event?: MouseEvent): void {
    const target = event?.target as HTMLElement | null;
    if (target?.closest('a') || target?.closest('button')) {
      return;
    }
    this.router?.navigate(['/templates', templateId, 'edit']);
  }

  onLimitChange(newLimit: number): void {
    const parsed = Number(newLimit);
    if (isNaN(parsed) || this.limit === parsed || this.loading) {
      return;
    }

    this.limit = parsed;
    this.page = 1;
    this.loadTemplates();
  }

  goToFirstPage(): void {
    this.goToPage(1);
  }

  goToLastPage(): void {
    this.goToPage(this.totalPages);
  }

  goToPage(targetPage: number): void {
    if (targetPage < 1 || targetPage > this.totalPages || targetPage === this.page || this.loading) {
      return;
    }
    this.page = targetPage;
    this.goToPageInput = String(targetPage);
    this.loadTemplates();
  }

  jumpToPage(): void {
    const raw = String(this.goToPageInput || '').trim();
    const target = parseInt(raw, 10);
    if (!isNaN(target) && target >= 1 && target <= this.totalPages) {
      this.goToPage(target);
    } else {
      this.goToPageInput = String(this.page);
    }
  }

  pageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(1, this.page - 2);
    let end = Math.min(this.totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  goToPreviousPage(): void {
    if (this.page <= 1 || this.loading) {
      return;
    }

    this.page -= 1;
    this.loadTemplates();
  }

  goToNextPage(): void {
    if (this.page >= this.totalPages || this.loading) {
      return;
    }

    this.page += 1;
    this.loadTemplates();
  }

  formatUpdatedAt(value: string): string {
    return this.dateTimeFormat.formatDateTime(value);
  }

  private loadTemplates(): void {
    this.loading = true;

    this.api
      .listTemplatesPage(this.page, this.limit, this.appliedSearch)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.feedback.clearError();
          this.errorMessage = '';
          this.templates = response.items;
          this.total = response.total;
          this.totalPages = Math.max(response.totalPages || 1, 1);
          this.page = response.page;
          this.goToPageInput = String(this.page);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, { fallbackMessage: 'Failed to load templates' });
          this.errorMessage = normalized.message;
          this.feedback.showError({
            ...normalized,
            actionLabel: 'Retry',
            action: () => this.loadTemplates(),
          });
          this.cdr.markForCheck();
        },
      });
  }
}
